/**
 * /superadmin/support — the Contact WorkRight ticket queue for the
 * platform support team (mockup §5). The count strip doubles as the main
 * filter; search, priority and assignee narrow it further. All filter
 * state lives in the URL.
 *
 * Backend: GET /api/v1/admin/support/tickets, …/counts.
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Inbox, Search } from "lucide-react";
import { z } from "zod";
import { toApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { SUPPORT_PRIORITY_LABELS, type SupportPriority } from "@/lib/support-tickets";
import type { SupportQueueParams } from "@/lib/api/support-admin";
import { useSupportCounts, useSupportQueue } from "@/hooks/use-support-tickets";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { SupportStatusPill } from "@/components/support/status-pill";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const VIEWS = ["waiting_on_us", "unassigned", "waiting_on_customer", "past_target", "all"] as const;
type View = (typeof VIEWS)[number];

const searchSchema = z.object({
  view: z.enum(VIEWS).optional(),
  q: z.string().max(100).optional(),
  priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
  assignee: z.enum(["me", "none"]).optional(),
  tenant_id: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/_authgated/superadmin/support/")({
  validateSearch: searchSchema,
  component: SupportQueuePage,
});

const OPEN_STATUSES = "open,in_progress";

function paramsFor(view: View, s: z.infer<typeof searchSchema>): SupportQueueParams {
  const p: SupportQueueParams = { limit: 100 };
  if (view === "waiting_on_us") p.status = OPEN_STATUSES;
  if (view === "unassigned") {
    p.status = "open,in_progress,waiting_on_customer";
    p.assignee = "none";
  }
  if (view === "waiting_on_customer") p.status = "waiting_on_customer";
  if (view === "past_target") p.past_target = "1";
  if (s.q) p.q = s.q;
  if (s.priority) p.priority = s.priority;
  if (s.assignee && view !== "unassigned") p.assignee = s.assignee;
  if (s.tenant_id) p.tenant_id = s.tenant_id;
  return p;
}

function SupportQueuePage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const view: View = search.view ?? "waiting_on_us";
  const countsQ = useSupportCounts();
  const queueQ = useSupportQueue(paramsFor(view, search));
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => setQ(search.q ?? ""), [search.q]);

  const set = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });

  const c = countsQ.data;
  const tiles: { view: View; label: string; n?: number }[] = [
    { view: "waiting_on_us", label: "Waiting on Us", n: c?.waiting_on_us },
    { view: "unassigned", label: "Unassigned", n: c?.unassigned },
    { view: "waiting_on_customer", label: "Waiting on Customer", n: c?.waiting_on_customer },
    { view: "past_target", label: "Past 1 Working Day", n: c?.past_target },
    { view: "all", label: "All Tickets" },
  ];
  const rows = queueQ.data ?? [];
  const filtered = !!(search.q || search.priority || search.assignee || search.tenant_id);

  return (
    <>
      <PageHeader title="Support" description="Tickets from customer companies. Reply within one working day." />

      <div role="group" aria-label="Queue" className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map((t) => {
          const active = view === t.view;
          return (
            <button
              key={t.view}
              type="button"
              aria-pressed={active}
              onClick={() => set({ view: t.view })}
              className={cn(
                "min-h-11 rounded-lg border p-3 text-left hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active && "border-primary bg-primary/5",
              )}
            >
              <span className="block text-xs text-muted-foreground">{t.label}</span>
              {t.n !== undefined && (
                <span
                  className={cn(
                    "block text-2xl font-semibold tabular-nums",
                    t.view === "past_target" && t.n > 0 && "text-destructive",
                  )}
                >
                  {t.n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <form
        className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: q.trim() || undefined });
        }}
      >
        <div className="flex-1 space-y-1">
          <Label htmlFor="support-q">Search</Label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="support-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="WR-1042, subject or company"
              className="pl-8"
            />
          </div>
        </div>
        <div className="space-y-1 sm:w-40">
          <Label htmlFor="support-priority">Priority</Label>
          <Select value={search.priority ?? "any"} onValueChange={(v) => set({ priority: v === "any" ? undefined : (v as SupportPriority) })}>
            <SelectTrigger id="support-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {(Object.keys(SUPPORT_PRIORITY_LABELS) as SupportPriority[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {SUPPORT_PRIORITY_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 sm:w-40">
          <Label htmlFor="support-assignee">Assignee</Label>
          <Select
            value={search.assignee ?? "any"}
            onValueChange={(v) => set({ assignee: v === "any" ? undefined : (v as "me" | "none") })}
          >
            <SelectTrigger id="support-assignee">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Anyone</SelectItem>
              <SelectItem value="me">Me</SelectItem>
              <SelectItem value="none">Unassigned</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
        {filtered && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ search: { view } })}
          >
            Clear Filters
          </Button>
        )}
      </form>

      {queueQ.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {queueQ.isError && (
        <Alert variant="destructive">
          <AlertDescription>{toApiError(queueQ.error).message}</AlertDescription>
        </Alert>
      )}
      {queueQ.data && rows.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={filtered ? "No tickets match these filters" : view === "all" ? "No tickets yet" : "You're all caught up"}
          action={
            filtered ? (
              <Button variant="outline" onClick={() => void navigate({ search: { view } })}>
                Clear Filters
              </Button>
            ) : undefined
          }
        />
      )}
      {rows.length > 0 && (
        <div role="region" aria-label="Tickets" tabIndex={0} className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid="support-queue-row">
                  <TableCell className="min-w-64">
                    <Link
                      to="/superadmin/support/$id"
                      params={{ id: String(t.id) }}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {t.subject}
                    </Link>
                    <span className="block font-mono text-xs text-muted-foreground">{t.number}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{t.company.name}</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap items-center gap-1.5">
                      <SupportStatusPill status={t.status} side="console" />
                      {t.past_target && (
                        <span className="whitespace-nowrap text-xs font-medium text-destructive">Past Target</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{SUPPORT_PRIORITY_LABELS[t.priority]}</TableCell>
                  <TableCell className="whitespace-nowrap">{t.assignee?.name ?? <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(t.last_activity_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
